import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { ScoreRing } from "../components/ScoreRing";
import { formatMetric } from "../format/formatMetric";
import { METRIC_HINTS } from "../model/METRIC_HINTS";
import { METRIC_LABELS } from "../model/METRIC_LABELS";
import { FindingCard } from "./FindingCard";
import { FindingHint } from "./FindingHint";
import type { FindingProps } from "./FindingProps";

const TITLE = msg({ id: "finding.metric", message: "{metric}: {value}" });

const DETAILS_BETTER = msg({
  id: "finding.metric.details_better",
  message:
    "{meaning} Better than {count} of 100 strong players on this hero; typical is {typical}.",
});

const DETAILS_WORSE = msg({
  id: "finding.metric.details_worse",
  message: "{meaning} Worse than {count} of 100 strong players on this hero; typical is {typical}.",
});

const HINT_LABEL = msg({ id: "finding.metric.hint", message: "More about: {metric}" });

const SUMMARY_BETTER = msg({
  id: "finding.metric.summary_better",
  message: "Better than {count} of 100 · typical {typical}",
});

const SUMMARY_WORSE = msg({
  id: "finding.metric.summary_worse",
  message: "Worse than {count} of 100 · typical {typical}",
});

const RING_LABEL = msg({
  id: "finding.metric.ring",
  message: "Better than {percentile} of 100",
});

// Метрика матча заметно хуже или лучше, чем у сильных игроков на том же герое.
export const MetricRated = ({ finding, variant }: FindingProps<"metric">) => {
  const { i18n } = useLingui();
  const { metric, value, typical } = finding.params;
  const percentile = Math.round(finding.params.percentile);
  const good = finding.severity === "good";
  const values = {
    count: good ? percentile : 100 - percentile,
    typical: formatMetric(metric, typical, i18n),
  };
  const label = i18n._(METRIC_LABELS[metric]);
  const hint = METRIC_HINTS[metric];
  return (
    <FindingCard
      code={finding.code}
      severity={finding.severity}
      variant={variant}
      icon={
        <ScoreRing
          score={percentile}
          size={variant === "lead" ? "md" : "sm"}
          severity={finding.severity}
          label={i18n._({ ...RING_LABEL, values: { percentile } })}
        />
      }
      title={i18n._({
        ...TITLE,
        values: { metric: label, value: formatMetric(metric, value, i18n) },
      })}
      details={i18n._({
        ...(good ? DETAILS_BETTER : DETAILS_WORSE),
        values: { ...values, meaning: i18n._(hint.meaning) },
      })}
      summary={i18n._({ ...(good ? SUMMARY_BETTER : SUMMARY_WORSE), values })}
      hint={
        <FindingHint
          label={i18n._({ ...HINT_LABEL, values: { metric: label } })}
          what={
            variant === undefined || variant === "full" || variant === "lead"
              ? undefined
              : i18n._(hint.meaning)
          }
          why={i18n._(hint.why)}
          fix={finding.severity === "good" ? undefined : i18n._(hint.fix)}
        />
      }
    />
  );
};
